import { FC, useEffect, useRef, useState } from 'react';
import styles from './DocumentCard.module.css';
import { Document, ErrorResponse } from '@/lib/types/apiTypes';
import { useAppDispatch } from '@/lib/hooks/hooks';
import { processStart, processSuccess } from '@/redux/slices/proposalSlice';
import { getDocumentFromSource } from '@/lib/functions/apiCalls';
import { openDialog } from '@/redux/slices/pageSlice';
import {
	handleApiError,
	handleGeneralError,
	showToast,
} from '@/lib/functions/funcUtils';
import PrimaryButton from '../ui/shared/Button/PrimaryButton/PrimaryButton';
import { cn } from '@/lib/utils';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { Trash2 } from 'lucide-react';

interface DocumentCardProps {
	documentCard: Document;
}

const DocumentCard: FC<DocumentCardProps> = ({ documentCard }) => {
	const [showFullText, setShowFullText] = useState(false);
	const cardRef = useRef<HTMLDivElement | null>(null);
	const textRef = useRef<HTMLParagraphElement | null>(null);
	const dispatch = useAppDispatch();

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
				setShowFullText(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
	const handlePDF = async () => {
		if (!documentCard?.sourceLink) {
			showToast(
				'error',
				'Something went wrong, please reload the page or try again later'
			);
			return;
		}
		dispatch(processStart());
		try {
			const response = await getDocumentFromSource(documentCard?.sourceLink);
			if (response) {
				// setPdfData(response as ArrayBuffer);
				dispatch(openDialog({ type: 'viewPDF', pdf: response as ArrayBuffer }));
				dispatch(processSuccess());
			}
		} catch (error: unknown) {
			if (error instanceof Error) {
				handleGeneralError(error);
				dispatch(processSuccess());
			} else if (
				typeof error === 'object' &&
				error !== null &&
				'message' in error
			) {
				const apiError = error as ErrorResponse;
				handleApiError(apiError);
				dispatch(processSuccess());
			}
		}
	};

	const handleDocDelete = () => {
		dispatch(
			openDialog({
				type: 'deleteDocument',
				documentId: documentCard?.id,
				documentName: documentCard?.name,
			})
		);
	};

	return (
		<div
			className={cn(styles.card, ' shadow-main rounded-xl p-5 ')}
			ref={cardRef}
		>
			<TooltipProvider delayDuration={50}>
				<Tooltip>
					<TooltipTrigger>
						<h4 className="h-[46px] text-start text-black font-bold mb-2 2xl:mb-4 dynamic-medium line-clamp-2">
							{documentCard?.name}
						</h4>
					</TooltipTrigger>
					<TooltipContent className="bg-white text-[#6D7D86] text-xs 2xl:text-sm px-3 2xl:px-5 2xl:py-1 rounded-full border-none shadow-none drop-shadow-effect">
						{documentCard?.name}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
			<p className="font-bold text-black ">
				Category:
				<span className="ml-1 text-base font-normal text-secondary xl:text-lg">
					{documentCard?.category}
				</span>
			</p>
			<div className="bg-[#E9E9E9] h-[1px] border-none my-3 xl:my-4"></div>
			<p
				ref={textRef}
				className={`text-sm xl:text-base text-black font-normal ${
					!showFullText ? styles.limitedText : ''
				}`}
			>
				{documentCard?.summary}
			</p>

			{documentCard?.summary?.length <
			(textRef?.current?.offsetWidth || 300) / 2.5 ? (
				<></>
			) : (
				<>
					{!showFullText && (
						<button
							className="text-sm xl:text-sm 2xl:text-base font-bold mt-2 text-[#5d9bfd]"
							onClick={() => setShowFullText(true)}
						>
							Read More
						</button>
					)}
					{showFullText && (
						<button
							className="text-sm xl:text-sm 2xl:text-base mt-2 text-[#5d9bfd]"
							onClick={() => setShowFullText(false)}
						>
							Less
						</button>
					)}
				</>
			)}
			<div className="flex items-center justify-between mt-5">
				<PrimaryButton onClick={handlePDF}>Read Document</PrimaryButton>
				<Trash2
					size={20}
					onClick={handleDocDelete}
					className="text-black cursor-pointer"
				/>
			</div>
		</div>
	);
};

export default DocumentCard;
